import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Polyline, Polygon as LPolygon, Marker } from "react-leaflet";
import { useMemo } from "react";
import { useConst, useConstCallback } from "@bryxinc/ts-utils";
import { DivIcon } from "leaflet";
import { motion } from "framer-motion";
import ReactDOM from "react-dom";
export function BryxDivMarker({ children, icon, iconAnchor, iconSize, ...props }) {
    const parent = useConst(() => {
        const div = document.createElement("div");
        div.style.height = "inherit";
        div.style.width = "inherit";
        return div;
    });
    const divIcon = useConst(() => new DivIcon({
        className: "",
        html: parent,
        iconAnchor,
        iconSize,
    }));
    return (_jsxs(_Fragment, { children: [_jsx(Marker, { ...props, icon: divIcon, children: children }), ReactDOM.createPortal(icon, parent)] }));
}
export const BryxPinIcon = ({ animate }) => {
    const children = (_jsx("svg", { width: "23px", height: "45px", viewBox: "0 0 23 45", version: "1.1", children: _jsx("g", { stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd", children: _jsxs("g", { id: "GenericPin", children: [_jsx("path", { d: "M11.57,44.7251201 C11.57,44.7251201 23.1399994,17.8088289 23.1399994,11.4726559 C23.1399994,5.13648303 17.9599341,0 11.5699997,0 C5.18006531,0 0,5.13648303 0,11.4726559 C0,17.8088289 11.57,44.7251201 11.57,44.7251201 Z", fill: "#C61A1B" }), _jsx("ellipse", { fill: "#FFFFFF", cx: "11.4910715", cy: "11.4112187", rx: "9.4910715", ry: "9.41121874" }), _jsx("circle", { fill: "#898989", cx: "11.5", cy: "11.5", r: "4.5" })] }) }) }));
    return animate ? (_jsx(motion.div, { initial: { translateY: "-100vh" }, animate: { translateY: "0vh" }, children: children })) : (children);
};
BryxPinIcon.useIcon = function useIcon(props) {
    return {
        iconSize: useConst([24, 45]),
        iconAnchor: useConst([11.5, 45]),
        icon: _jsx(BryxPinIcon, { ...props }),
    };
};
const PolygonDot = ({ lat, lng, onClick, }) => {
    const handlers = useConst({
        click: (event) => {
            onClick(event, lat, lng);
        },
    });
    return (_jsx(BryxDivMarker, { position: useMemo(() => [lat, lng], [lat, lng]), eventHandlers: handlers, iconSize: useConst([8, 8]), iconAnchor: useConst([4, 4]), icon: _jsx("svg", { viewBox: "0 0 2 2", height: 8, width: 8, style: { verticalAlign: "top" }, fill: PATH_COLORS.color, children: _jsx("circle", { cx: "1", cy: "1", r: "1" }) }) }, `${lat},${lng}`));
};
const UserSelectionMarkerPoint = ({ lat, lng, onClick, animate, }) => {
    const handlers = useConst({
        click: (event) => {
            onClick(event, lat, lng);
        },
    });
    return (_jsx(BryxDivMarker, { position: useMemo(() => [lat, lng], [lat, lng]), eventHandlers: handlers, ...BryxPinIcon.useIcon({ animate }) }, `${lat},${lng}`));
};
function arrayEquals(a, b) {
    return a.length == b.length && !a.some((value, index) => value !== b[index]);
}
const PATH_COLORS = { color: "red" };
export const UserSelectionMarker = ({ geometry, onClick, }) => {
    if (geometry.type == "Point") {
        const lat = geometry.coordinates[1];
        const lng = geometry.coordinates[0];
        return _jsx(UserSelectionMarkerPoint, { lat: lat, lng: lng, onClick: onClick });
    }
    else if (geometry.type == "Polygon") {
        const coordinates = geometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
        const finalized = arrayEquals(geometry.coordinates[0][0], geometry.coordinates[0][geometry.coordinates[0].length - 1]);
        const lines = finalized ? (_jsx(LPolygon, { pathOptions: PATH_COLORS, positions: coordinates }, `poly-${coordinates}`)) : (_jsx(Polyline, { pathOptions: PATH_COLORS, positions: coordinates }, `poly-${coordinates}`));
        return (_jsxs(_Fragment, { children: [lines, ...geometry.coordinates[0].map(([lng, lat]) => _jsx(PolygonDot, { lat: lat, lng: lng, onClick: onClick }))] }));
    }
    else {
        throw new Error("Unreachable code!");
    }
};
// Little convenience markers
export const BryxPolyMarker = ({ polygon, onClick }) => {
    const coordinates = polygon.coordinates[0].map(([lng, lat]) => [lat, lng]);
    const handlers = useConst({
        click: useConstCallback((event) => {
            onClick?.(event);
        }),
    });
    return _jsx(LPolygon, { pathOptions: PATH_COLORS, positions: coordinates, eventHandlers: onClick ? handlers : undefined });
};
export const BryxPointMarker = ({ point, onClick, animate, }) => {
    const onClickWrap = useConstCallback((event, _lat, _lng) => {
        onClick?.(event);
    });
    return _jsx(UserSelectionMarkerPoint, { onClick: onClickWrap, lat: point.coordinates[1], lng: point.coordinates[0], animate: animate });
};
